import { Database, Activity, Edit2, RotateCcw, Plus } from "lucide-react";

export default function SQLSection({
  hasSql,
  sqlConfig,
  onAdd,
  onEdit,
  onReingest,
}) {
  return (
    <section>
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
          <Database size={20} className="text-amber-500" /> SQL Analytics
          Integration
        </h3>
        <button
          onClick={onAdd}
          className="text-sm font-semibold text-[#1d57a5] dark:text-[#4a8ce3] flex items-center gap-1.5 bg-[#1d57a5]/10 dark:bg-[#4a8ce3]/20 px-3 py-1.5 rounded-lg transition-colors hover:bg-[#1d57a5]/20"
        >
          <Plus size={16} /> Add Configuration
        </button>
      </div>

      {hasSql && sqlConfig ? (
        <div className="bg-slate-50 dark:bg-slate-900/30 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm">
          {/* Connection header */}
          <div className="p-5 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex justify-between items-center">
            <div>
              <p className="text-sm font-bold text-slate-800 dark:text-white flex items-center gap-2">
                <Activity size={16} className="text-green-500" />
                {sqlConfig.dbType} Connection Active
              </p>
              <p className="text-xs text-slate-500 mt-1 pl-6">
                Server:{" "}
                <span className="font-mono">{sqlConfig.serverName}</span>:
                {sqlConfig.portNum}
              </p>
              <p className="text-xs text-slate-500 pl-6">
                Database:{" "}
                <span className="font-mono">{sqlConfig.databaseName}</span>
                {sqlConfig.schemaName !== "-" && (
                  <>
                    {" "}
                    · Schema:{" "}
                    <span className="font-mono">{sqlConfig.schemaName}</span>
                  </>
                )}
              </p>
            </div>
            <button
              onClick={onEdit}
              className="p-2 bg-slate-100 dark:bg-slate-700 text-slate-500 hover:text-[#1d57a5] rounded-lg transition-colors"
            >
              <Edit2 size={16} />
            </button>
          </div>

          {/* Tables list */}
          <ul className="divide-y divide-slate-100 dark:divide-slate-700/50">
            {sqlConfig.tablesArray.length > 0 ? (
              sqlConfig.tablesArray.map((table) => (
                <li
                  key={table}
                  className="p-5 flex items-center justify-between hover:bg-white dark:hover:bg-slate-800 transition-colors group"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-500 flex items-center justify-center">
                      <Database size={16} />
                    </div>
                    <div>
                      <span className="text-sm font-semibold font-mono text-slate-700 dark:text-slate-300 block mb-1">
                        {table}
                      </span>
                      <span className="px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400">
                        Indexed &amp; Ready
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => onReingest(table)}
                      className="px-3 py-1.5 bg-slate-100 hover:bg-[#1d57a5] hover:text-white dark:bg-slate-700 dark:hover:bg-[#4a8ce3] text-slate-600 dark:text-slate-300 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5"
                    >
                      <RotateCcw size={14} /> Re-ingest
                    </button>
                  </div>
                </li>
              ))
            ) : (
              <li className="p-8 text-center text-slate-500 text-sm flex flex-col items-center">
                <Database
                  size={24}
                  className="text-slate-300 dark:text-slate-600 mb-2"
                />
                No tables configured for this connection.
              </li>
            )}
          </ul>

          {/* Summary footer */}
          <div className="px-5 py-3 bg-slate-50 dark:bg-slate-900/20 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between">
            <span className="text-xs text-slate-500">
              {sqlConfig.tablesArray.length} table
              {sqlConfig.tablesArray.length !== 1 ? "s" : ""} ·{" "}
              {sqlConfig.viewsArray.length} view
              {sqlConfig.viewsArray.length !== 1 ? "s" : ""} synced
            </span>
            <span className="inline-flex px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-500 font-bold text-xs rounded-md border border-amber-200 dark:border-amber-800">
              {sqlConfig.tablesArray.length} Synced
            </span>
          </div>
        </div>
      ) : (
        <div className="border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-3xl p-10 text-center flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-900/30">
          <div className="w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/20 text-amber-500 flex items-center justify-center mb-4">
            <Database size={32} />
          </div>
          <p className="text-base font-bold text-slate-800 dark:text-slate-200 mb-1">
            SQL Analytics not enabled
          </p>
          <p className="text-sm text-slate-500 mb-6 max-w-sm">
            Connect a database schema to enable generative SQL analytics for
            this tenant.
          </p>
          <button
            onClick={onAdd}
            className="px-6 py-2.5 bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-500 hover:bg-amber-200 dark:hover:bg-amber-900/60 rounded-full text-sm font-bold transition-all hover:shadow-md"
          >
            Configure Connection
          </button>
        </div>
      )}
    </section>
  );
}